import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Text, Heading, ProgressCircle } from "@react-spectrum/s2";
import { GlobeGrid as Globe, History, Settings } from '../icons/s2';
import { CCSite, CCDomainConfigurationEntry } from '../../types';
import { fetchCCSiteDetail, fetchDomainConfiguration } from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import { FieldCard, renderConfigFields } from './FieldCard';
import { SurfaceCard } from '../common/SurfaceCard';
import { ConfigurationStatusBadges } from '../SiteCard/UtilityConfigurationPicker';
import { utilities } from '../Models/modelUtilities';
export interface DomainDetailPanelProps {
    siteId: string;
    orgId: string;
    /** Notified once the site detail loads, so a parent header (e.g. the org
   * detail page title) can show the real site name even when the site isn't
   * present in whatever page of the domains list happens to be loaded. */ onSiteLoaded?: (site: CCSite) => void;
}
function utilityLabel(utility: string, t: TFunction): string {
    return utilities(t).find((u)=>u.key === utility)?.label ?? (utility.charAt(0).toUpperCase() + utility.slice(1));
}
export const DomainDetailPanel: React.FC<DomainDetailPanelProps> = ({ siteId, orgId, onSiteLoaded })=>{
    const { t } = useTranslation();
    const [site, setSite] = useState<CCSite | null>(null);
    const [utilityConfigurations, setUtilityConfigurations] = useState<Record<string, CCDomainConfigurationEntry>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const loadSiteDetails = useCallback(async ()=>{
        try {
            setLoading(true);
            setError(null);
            const [siteDetails, configuration] = await Promise.all([
                fetchCCSiteDetail(siteId, orgId),
                fetchDomainConfiguration(orgId, siteId)
            ]);
            setSite(siteDetails);
            setUtilityConfigurations(configuration.utility_configurations ?? {});
            onSiteLoaded?.(siteDetails);
        } catch (err) {
            setError(err instanceof Error ? err : new Error(t('commandCenter.domainDetail.failedToLoad')));
        } finally{
            setLoading(false);
        }
    // onSiteLoaded intentionally omitted: callers pass an inline setter, and
    // including it would re-run this fetch on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        siteId,
        orgId,
        t
    ]);
    useEffect(()=>{
        loadSiteDetails();
    }, [
        loadSiteDetails
    ]);
    if (loading) {
        return (<div className={" sd171 _ta171 eb171 _Ca171 MDvO7ld171"}>
        <ProgressCircle aria-label={t('commandCenter.domainDetail.loading')} isIndeterminate/>
      </div>);
    }
    if (error) {
        return (<div className={" sd171 _ta171 eb171 _Ca171 MDvO7ld171"}>
        <Text UNSAFE_className="cc-error-text">{error.message}</Text>
      </div>);
    }
    if (!site) return null;
    return (<div className={" sd171 _ta171 Ul171 ql171 Lz6EEyb171"}>
      <SurfaceCard stack>
        <div className={" sd171 _tc171 eb171 Ue171 qe171"}>
          <Globe size="S"/>
          <Heading level={4} styles={" Jy171 Gy171 Iy171 Hy171"}>{t('commandCenter.domainDetail.domainInformation')}</Heading>
        </div>
        <div className={" sd171 _ta171 Ul171 ql171"}>
          <FieldCard label={t('commandCenter.domainDetail.fields.domainId')} value={site.site_id}/>
          <FieldCard label={t('commandCenter.domainDetail.fields.domainName')} value={site.site_name}/>
          <FieldCard label={t('commandCenter.domainDetail.fields.description')} value={site.description || '-'}/>
          <FieldCard label={t('commandCenter.domainDetail.fields.organizationId')} value={orgId}/>
        </div>
      </SurfaceCard>

      {Object.entries(utilityConfigurations).map(([utility, entry])=>(<SurfaceCard key={utility} stack>
          <div className={" sd171 _tc171 eb171 _Ce171"}>
            <div className={" sd171 _tc171 eb171 Ue171 qe171"}>
              <Settings size="S"/>
              <Heading level={4} styles={" Jy171 Gy171 Iy171 Hy171"}>{t('commandCenter.domainDetail.utilityConfiguration', {
            utility: utilityLabel(utility, t)
        })}</Heading>
            </div>
            <ConfigurationStatusBadges status={entry}/>
          </div>
          {entry.is_deprecated && entry.sunset_date && (<Text UNSAFE_className="cc-empty-config-text">{t('commandCenter.domainDetail.sunset', {
            date: formatDate(entry.sunset_date)
        })}</Text>)}
          <div className={" sd171 _ta171 Ul171 ql171"}>
            <FieldCard label={t('commandCenter.domainDetail.fields.configurationName')} value={entry.configuration_name}/>
            {renderConfigFields(entry.credentials)}
            {renderConfigFields(entry.behavioural_parameters as Record<string, unknown>)}
          </div>
        </SurfaceCard>))}

      <SurfaceCard stack>
        <div className={" sd171 _tc171 eb171 Ue171 qe171"}>
          <History size="S"/>
          <Heading level={4} styles={" Jy171 Gy171 Iy171 Hy171"}>{t('commandCenter.domainDetail.auditInformation')}</Heading>
        </div>
        <div className={" sd171 _ta171 Ul171 ql171"}>
          <FieldCard label={t('commandCenter.domainDetail.fields.createdBy')} value={site.created_by}/>
          <FieldCard label={t('commandCenter.domainDetail.fields.createdAt')} value={formatDate(site.created_at)}/>
          <FieldCard label={t('commandCenter.domainDetail.fields.updatedBy')} value={site.updated_by || '-'}/>
          <FieldCard label={t('commandCenter.domainDetail.fields.updatedAt')} value={site.updated_at ? formatDate(site.updated_at) : '-'}/>
        </div>
      </SurfaceCard>
    </div>);
};

import "macro-9575a432412d01bc2072569f07da0d02a1c75f4fe2f28fa8e0a2e7e3ef4d0087.css";
import "macro-9575a432412d01bc2072569f07da0d02a1c75f4fe2f28fa8e0a2e7e3ef4d0087.css";
import "macro-e65a6cd981b335be1f7a9c3f0c4189ac4e5c125e5d662cb84d4ed6a7cd3745c2.css";
import "macro-c1831db95f3c4919d0e4a6787b498c998368c0f594c8972a5c4a511e6dd53092.css";
import "macro-9265ce547da9b2dc6ffe8b0a3109d3facb0513bbd657f7ae9beeb5d91b71740f.css";
import "macro-e77d45646f8bdb461ee9552438da3de2016a7864660e2aa4864c36c173c39db6.css";
import "macro-59782c8b48ec8051d4948915909049426f33894b5e24dcd06bbddcc6622b89a3.css";
import "macro-c1831db95f3c4919d0e4a6787b498c998368c0f594c8972a5c4a511e6dd53092.css";
import "macro-9265ce547da9b2dc6ffe8b0a3109d3facb0513bbd657f7ae9beeb5d91b71740f.css";
import "macro-e77d45646f8bdb461ee9552438da3de2016a7864660e2aa4864c36c173c39db6.css";
import "macro-c1831db95f3c4919d0e4a6787b498c998368c0f594c8972a5c4a511e6dd53092.css";
import "macro-9265ce547da9b2dc6ffe8b0a3109d3facb0513bbd657f7ae9beeb5d91b71740f.css";
import "macro-e77d45646f8bdb461ee9552438da3de2016a7864660e2aa4864c36c173c39db6.css";